// Saved decks. Rendered as a metadata rail — each row is the deck's
// theme (display-serif) with a mono metadata sub-line (proximity score,
// cost, speed, age). No card grid. No heavy borders. Character via
// typography + rhythm. Editorial empty state for the no-decks case.

import { useEffect, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Globe, Search, Trash2, X } from "lucide-react";
import {
  deleteDeck,
  discardBuild,
  listDecks,
  listInProgressBuilds,
  listPublicDecks,
  type BuildSummary,
  type SavedDeckSummary,
} from "../lib/api";
import ProximityBadge from "../components/ProximityBadge";
import EmptyState from "../components/EmptyState";
import Kbd from "../components/Kbd";
import { cn } from "../lib/cn";

function fmtRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const delta = Date.now() - then;
  const m = Math.floor(delta / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

type DeckTab = "mine" | "public";

export default function DeckList() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<DeckTab>("mine");
  // Search input is uncontrolled-feeling but tracked for debounced
  // cache-key updates. `query` is what the user typed; `debouncedQuery`
  // is what feeds the request — bumped 250ms after typing stops so we
  // don't fire a request per keystroke. Empty/whitespace = no filter.
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["decks", debouncedQuery],
    queryFn: () => listDecks(debouncedQuery || undefined),
    // Keep the previous results visible while a new search request is
    // in flight so the list doesn't blank-flash on every keystroke
    // settle. The search input has its own indicator below.
    placeholderData: (prev) => prev,
  });
  // Public-decks query is lazy: only fires once the user actually opens
  // the Public tab. Caches across tab switches.
  const publicQuery = useQuery({
    queryKey: ["public-decks"],
    queryFn: () => listPublicDecks(50, 0),
    enabled: tab === "public",
    staleTime: 60_000,
  });
  // In-progress builds (running, waiting, errored). Poll every 8s so a
  // build that finishes in another tab drops out of the list here.
  const { data: builds } = useQuery({
    queryKey: ["builds", "in-progress"],
    queryFn: listInProgressBuilds,
    refetchInterval: 8_000,
  });

  // Which deck the user is confirming deletion for. null = no dialog open.
  // Keep the full summary (not just id) so the dialog can quote the theme
  // in the "Delete 'X'?" prompt without a second lookup.
  const [pending, setPending] = useState<SavedDeckSummary | null>(null);
  const [pendingBuild, setPendingBuild] = useState<BuildSummary | null>(null);

  const delMut = useMutation({
    mutationFn: (id: string) => deleteDeck(id),
    onSuccess: (_res, id) => {
      // Optimistic patch across every active ["decks", q] cache so the
      // deleted row disappears immediately whether the user is filtering
      // or browsing the unfiltered list. Refetch still runs for
      // authoritative state.
      qc.setQueriesData<{ count: number; decks: SavedDeckSummary[] }>(
        { queryKey: ["decks"] },
        (prev) =>
          prev
            ? {
                count: prev.count - 1,
                decks: prev.decks.filter((d) => d.id !== id),
              }
            : prev,
      );
      void qc.invalidateQueries({ queryKey: ["decks"] });
    },
  });

  // Discard covers both non-terminal (running/awaiting_input) and errored
  // builds — the server abandons the row and refunds credits if any are
  // still held. Optimistic-patch the in-progress list for the same
  // no-flash feel as deck deletion.
  const discardMut = useMutation({
    mutationFn: (id: string) => discardBuild(id),
    onSuccess: (_res, id) => {
      qc.setQueryData<{ count: number; builds: BuildSummary[] }>(
        ["builds", "in-progress"],
        (prev) =>
          prev
            ? {
                count: Math.max(0, prev.count - 1),
                builds: prev.builds.filter((b) => b.id !== id),
              }
            : prev,
      );
      void qc.invalidateQueries({ queryKey: ["builds", "in-progress"] });
    },
  });

  return (
    <div className="mx-auto max-w-5xl px-6 md:px-10 py-10 space-y-10">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Library</div>
          <h1 className="mt-2 font-display text-3xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.0]">
            Saved decks.
          </h1>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-fg-subtle">
          {data && (
            <span className="font-mono tabular-nums">{data.count} total</span>
          )}
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-fg-muted hover:text-fg"
          >
            New deck
            <Kbd>N</Kbd>
          </Link>
          <span className="inline-flex items-center gap-1.5 text-fg-muted">
            Jump
            <Kbd combo="⌘K" />
          </span>
        </div>
      </header>

      <nav
        role="tablist"
        aria-label="Deck library tabs"
        className="flex items-center gap-1 border-b border-border/60"
      >
        <TabButton active={tab === "mine"} onClick={() => setTab("mine")}>
          Mine
          {data && (
            <span className="ml-1.5 text-[10px] font-mono tabular-nums text-fg-subtle">
              {data.count}
            </span>
          )}
        </TabButton>
        <TabButton active={tab === "public"} onClick={() => setTab("public")}>
          <Globe size={12} strokeWidth={1.75} className="inline-block mr-1 -mt-0.5" />
          Public
          {publicQuery.data && (
            <span className="ml-1.5 text-[10px] font-mono tabular-nums text-fg-subtle">
              {publicQuery.data.count}
            </span>
          )}
        </TabButton>
      </nav>

      {tab === "mine" && (
        <div className="relative">
          <Search
            size={14}
            strokeWidth={1.75}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-subtle"
            aria-hidden="true"
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by theme, brief, archetype, or card name…"
            aria-label="Search saved decks"
            className="input w-full pl-9 pr-9 text-sm"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-7 w-7 rounded-md text-fg-subtle hover:text-fg hover:bg-bg-raised/60 focus:outline-none focus:ring-1 focus:ring-border"
            >
              <X size={14} strokeWidth={1.75} />
            </button>
          )}
        </div>
      )}

      {tab === "mine" && isLoading && (
        <ul className="divide-y divide-border/60">
          {Array.from({ length: 4 }).map((_, i) => (
            <li key={i} className="py-5 space-y-2">
              <div className="skeleton h-5 w-2/3" />
              <div className="skeleton h-3 w-1/2" />
            </li>
          ))}
        </ul>
      )}

      {tab === "mine" && error && (
        <div className="surface p-4 text-sm text-danger">
          {(error as Error).message}
        </div>
      )}

      {tab === "mine" && builds && builds.builds.length > 0 && (
        <section className="space-y-3">
          <div className="eyebrow">In progress</div>
          <ul className="divide-y divide-border/60">
            {builds.builds.map((b) => (
              <BuildRow
                key={b.id}
                build={b}
                onRequestDiscard={() => setPendingBuild(b)}
              />
            ))}
          </ul>
        </section>
      )}

      {tab === "mine" && data && data.decks.length === 0 && !debouncedQuery && (
        <EmptyState
          title="No decks saved yet."
          body="Tell the Oracle what you want — a win condition, a matchup, a budget — and your first 60-card list lands in this library."
          action={
            <Link to="/" className="btn-primary text-sm h-9 px-4">
              Build a deck
            </Link>
          }
          tip={<>tip: press <Kbd>⌘K</Kbd> from any page to jump around</>}
        />
      )}

      {tab === "mine" && data && data.decks.length === 0 && debouncedQuery && (
        <div className="text-sm text-fg-subtle py-6">
          No decks match{" "}
          <span className="font-mono text-fg-muted">“{debouncedQuery}”</span>.{" "}
          <button
            type="button"
            onClick={() => setQuery("")}
            className="text-fg-muted underline-offset-2 hover:underline"
          >
            Clear search
          </button>
          .
        </div>
      )}

      {tab === "mine" && data && data.decks.length > 0 && (
        <ul className="divide-y divide-border/60">
          {data.decks.map((d) => (
            <DeckRow
              key={d.id}
              deck={d}
              onRequestDelete={() => setPending(d)}
            />
          ))}
        </ul>
      )}

      {tab === "public" && (
        <PublicDeckTab
          loading={publicQuery.isLoading}
          error={publicQuery.error as Error | null}
          decks={publicQuery.data?.decks ?? []}
        />
      )}

      <DeleteDeckDialog
        deck={pending}
        pending={delMut.isPending}
        error={delMut.error as Error | null}
        onCancel={() => {
          if (delMut.isPending) return;
          delMut.reset();
          setPending(null);
        }}
        onConfirm={() => {
          if (!pending) return;
          delMut.mutate(pending.id, {
            onSuccess: () => setPending(null),
          });
        }}
      />

      <DiscardBuildDialog
        build={pendingBuild}
        pending={discardMut.isPending}
        error={discardMut.error as Error | null}
        onCancel={() => {
          if (discardMut.isPending) return;
          discardMut.reset();
          setPendingBuild(null);
        }}
        onConfirm={() => {
          if (!pendingBuild) return;
          discardMut.mutate(pendingBuild.id, {
            onSuccess: () => setPendingBuild(null),
          });
        }}
      />
    </div>
  );
}

const BUILD_STATUS_META: Record<
  BuildSummary["status"],
  { label: string; tone: string }
> = {
  running: { label: "running", tone: "text-accent" },
  awaiting_input: { label: "waiting for you", tone: "text-warn" },
  error: { label: "errored", tone: "text-danger" },
  complete: { label: "complete", tone: "text-accent" },
  abandoned: { label: "abandoned", tone: "text-fg-muted" },
};

function BuildRow({
  build,
  onRequestDiscard,
}: {
  build: BuildSummary;
  onRequestDiscard: () => void;
}) {
  const age = fmtRelative(build.started_at);
  const { label: statusLabel, tone: statusTone } = BUILD_STATUS_META[build.status];
  return (
    <li className="relative group/row">
      <Link
        to={`/?resume=${encodeURIComponent(build.id)}`}
        className="group grid grid-cols-[1fr_auto] items-baseline gap-x-6 gap-y-1 py-4 pr-10 hover:bg-bg-raised/30 -mx-3 px-3 rounded-md transition-colors"
      >
        <div className="flex items-baseline gap-3 min-w-0">
          <div className="font-display text-base md:text-lg font-medium tracking-[-0.01em] truncate">
            {build.brief}
          </div>
          <span
            className={cn(
              "font-mono text-[10px] uppercase tracking-wider shrink-0",
              statusTone,
            )}
          >
            {statusLabel}
          </span>
        </div>
        <div className="font-mono tabular-nums text-xs text-fg-subtle shrink-0 text-right">
          started {age}
        </div>
        {build.error && (
          <div className="col-span-2 text-xs text-danger/80 line-clamp-1">
            {build.error}
          </div>
        )}
        {build.status === "awaiting_input" && build.pending_question && (
          <div className="col-span-2 text-sm text-fg-muted italic line-clamp-2">
            “{build.pending_question}”
          </div>
        )}
        <div className="col-span-2 font-mono text-[11px] text-fg-subtle">
          <span className="text-fg-subtle/70">
            {build.status === "awaiting_input" ? "resume and answer" : "resume"}
          </span>
          <span className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
            →
          </span>
        </div>
      </Link>

      {/* Discard affordance — outside the Link so the anchor stays valid.
          Labeled by status so the intent reads right for errored rows. */}
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onRequestDiscard();
        }}
        aria-label={
          build.status === "error"
            ? `Dismiss errored build: ${build.brief}`
            : `Discard in-progress build: ${build.brief}`
        }
        className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-8 w-8 rounded-md text-fg-subtle hover:text-danger hover:bg-bg-raised/60 opacity-0 group-hover/row:opacity-100 focus:opacity-100 focus:outline-none focus:ring-1 focus:ring-border transition-opacity"
      >
        <Trash2 size={15} strokeWidth={1.75} />
      </button>
    </li>
  );
}

function DeckRow({
  deck,
  onRequestDelete,
}: {
  deck: SavedDeckSummary;
  onRequestDelete: () => void;
}) {
  const showCost = typeof deck.card_cost_usd === "number" && deck.card_cost_usd > 0;
  const costPrefix = (deck.card_cost_missing ?? 0) > 0 ? "≥ " : "";

  return (
    <li className="relative group/row">
      <Link
        to={`/decks/${deck.owner_username}/${deck.id}`}
        className="group grid grid-cols-[1fr_auto] items-baseline gap-x-6 gap-y-1 py-5 hover:bg-bg-raised/30 -mx-3 px-3 rounded-md transition-colors"
      >
        {/* Row 1: title + proximity */}
        <div className="flex items-baseline gap-3 min-w-0">
          <div className="font-display text-lg md:text-xl font-medium tracking-[-0.01em] truncate">
            {deck.theme}
          </div>
          <div className="shrink-0">
            <ProximityBadge score={deck.proximity_score} />
          </div>
        </div>
        <div className="font-mono tabular-nums text-sm text-fg-subtle shrink-0 text-right">
          {fmtRelative(deck.created_at)}
        </div>

        {/* Row 2: the brief — one-liner */}
        <div className="col-span-2 text-sm text-fg-muted line-clamp-1">
          {deck.brief}
        </div>

        {/* Row 3: mono metadata rail */}
        <div className="col-span-2 flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px] text-fg-subtle tabular-nums">
          <span className="inline-flex items-baseline gap-1.5">
            <span className="text-fg-subtle/70">built</span>
            <span className="text-fg-muted">{(deck.elapsed_ms / 1000).toFixed(1)}s</span>
          </span>
          {showCost && (
            <>
              <span className="text-fg-subtle/50">·</span>
              <span className="inline-flex items-baseline gap-1.5">
                <span className="text-fg-subtle/70">price</span>
                <span
                  className="text-fg-muted"
                  title={
                    (deck.card_cost_missing ?? 0) > 0
                      ? `Deck total at Scryfall prices — ${deck.card_cost_missing} copies missing price`
                      : "Deck total at Scryfall prices"
                  }
                >
                  {costPrefix}${deck.card_cost_usd!.toFixed(0)}
                </span>
              </span>
            </>
          )}
          <span className="text-fg-subtle/50">·</span>
          <span className="inline-flex items-baseline gap-1.5">
            <span className="text-fg-subtle/70">model</span>
            <span className="text-fg-muted truncate max-w-[18ch]" title={deck.model}>
              {deck.model.split(".").pop()}
            </span>
          </span>
          {/* Version chip — only shown when the deck has been iterated
              past v1. Every deck starts at v1 (the build snapshot), so
              showing it on unedited decks would just be noise. */}
          {typeof deck.version_no === "number" && deck.version_no >= 2 && (
            <>
              <span className="text-fg-subtle/50">·</span>
              <span
                className="inline-flex items-baseline gap-1.5"
                title={`Currently on v${deck.version_no} — open the deck to see edit history`}
              >
                <span className="text-fg-subtle/70">version</span>
                <span className="text-fg-muted">v{deck.version_no}</span>
              </span>
            </>
          )}
          <span className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-fg-subtle">
            →
          </span>
        </div>
      </Link>

      {/* Delete affordance lives OUTSIDE the <Link> so the button remains
          valid interactive HTML (no nested <a><button>). Hover-reveal on
          the row; visible on focus-within for keyboard use. */}
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onRequestDelete();
        }}
        aria-label={`Delete deck: ${deck.theme}`}
        className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-8 w-8 rounded-md text-fg-subtle hover:text-danger hover:bg-bg-raised/60 opacity-0 group-hover/row:opacity-100 focus:opacity-100 focus:outline-none focus:ring-1 focus:ring-border transition-opacity"
      >
        <Trash2 size={15} strokeWidth={1.75} />
      </button>
    </li>
  );
}

// Two-state confirm: idle + deleting. Escape cancels (when not busy),
// Enter fires delete. Errors surface inline so the user can retry
// without losing the dialog.
function DeleteDeckDialog({
  deck,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  deck: SavedDeckSummary | null;
  pending: boolean;
  error: Error | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  useEffect(() => {
    if (!deck) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !pending) onCancel();
      if (e.key === "Enter" && !pending) onConfirm();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [deck, pending, onCancel, onConfirm]);

  if (!deck) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-deck-title"
    >
      <div
        className="surface w-full max-w-md p-6 space-y-4 shadow-card-hover"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <h2 id="delete-deck-title" className="text-base font-semibold">
            Delete this deck?
          </h2>
          <button
            onClick={onCancel}
            disabled={pending}
            className="text-fg-subtle hover:text-fg transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <X size={16} strokeWidth={1.75} />
          </button>
        </div>

        <div className="text-sm text-fg-muted space-y-2">
          <p>
            <span className="font-display text-fg">{deck.theme}</span> will be
            removed from your library. Its decklist, strategy write-up, and
            build trace go with it.
          </p>
          <p className="text-xs text-fg-subtle">This cannot be undone.</p>
        </div>

        {error && (
          <div className="text-xs text-danger">
            {error.message || "delete failed"}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            className="btn-secondary text-sm"
            onClick={onCancel}
            disabled={pending}
          >
            Cancel
          </button>
          <button
            className="btn-primary text-sm"
            onClick={onConfirm}
            disabled={pending}
          >
            {pending ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "px-3 py-2 text-sm font-medium transition-colors -mb-px border-b-2",
        active
          ? "border-fg text-fg"
          : "border-transparent text-fg-muted hover:text-fg",
      )}
    >
      {children}
    </button>
  );
}

// Public-tab body. Read-only listing; click-through goes to the standard
// /decks/:slug route, which Layer C's getDeckOrPublic resolves to a
// public-deck view when the slug isn't owned by the caller.
function PublicDeckTab({
  loading,
  error,
  decks,
}: {
  loading: boolean;
  error: Error | null;
  decks: SavedDeckSummary[];
}) {
  if (loading) {
    return (
      <ul className="divide-y divide-border/60">
        {Array.from({ length: 3 }).map((_, i) => (
          <li key={i} className="py-5 space-y-2">
            <div className="skeleton h-5 w-2/3" />
            <div className="skeleton h-3 w-1/2" />
          </li>
        ))}
      </ul>
    );
  }
  if (error) {
    return (
      <div className="surface p-4 text-sm text-danger">{error.message}</div>
    );
  }
  if (decks.length === 0) {
    return (
      <div className="text-sm text-fg-subtle">
        No public decks yet. The seeded gauntlet meta archetypes show up
        here once Phase 1 of the seed has run.
      </div>
    );
  }
  return (
    <ul className="divide-y divide-border/60">
      {decks.map((d) => (
        <PublicDeckRow key={`${d.owner_username}/${d.id}`} deck={d} />
      ))}
    </ul>
  );
}

function PublicDeckRow({ deck }: { deck: SavedDeckSummary }) {
  const isMeta = deck.role === "meta_reference";
  const showCost = typeof deck.card_cost_usd === "number" && deck.card_cost_usd > 0;
  const costPrefix = (deck.card_cost_missing ?? 0) > 0 ? "≥ " : "";
  return (
    <li>
      <Link
        to={`/decks/${deck.owner_username}/${deck.id}`}
        className="group grid grid-cols-[1fr_auto] items-baseline gap-x-6 gap-y-1 py-5 hover:bg-bg-raised/30 -mx-3 px-3 rounded-md transition-colors"
      >
        <div className="flex items-baseline gap-3 min-w-0">
          <div className="font-display text-lg md:text-xl font-medium tracking-[-0.01em] truncate">
            {deck.theme}
          </div>
          {isMeta && (
            <span
              className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-700 dark:text-amber-400"
              title="Curated meta archetype used by the gauntlet"
            >
              meta
            </span>
          )}
          <ProximityBadge score={deck.proximity_score} />
        </div>
        <div className="font-mono tabular-nums text-sm text-fg-subtle shrink-0 text-right">
          {fmtRelative(deck.created_at)}
        </div>

        <div className="col-span-2 text-sm text-fg-muted line-clamp-1">
          {deck.brief}
        </div>

        <div className="col-span-2 flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px] text-fg-subtle tabular-nums">
          {deck.owner_username && (
            <>
              <span className="inline-flex items-baseline gap-1.5">
                <span className="text-fg-subtle/70">by</span>
                <span className="text-fg-muted">{deck.owner_username}</span>
              </span>
              <span className="text-fg-subtle/50">·</span>
            </>
          )}
          {showCost && (
            <>
              <span className="inline-flex items-baseline gap-1.5">
                <span className="text-fg-subtle/70">price</span>
                <span className="text-fg-muted">
                  {costPrefix}${deck.card_cost_usd!.toFixed(0)}
                </span>
              </span>
              <span className="text-fg-subtle/50">·</span>
            </>
          )}
          <span className="inline-flex items-baseline gap-1.5">
            <span className="text-fg-subtle/70">model</span>
            <span className="text-fg-muted truncate max-w-[18ch]" title={deck.model}>
              {deck.model.split(".").pop()}
            </span>
          </span>
          <span className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-fg-subtle">
            →
          </span>
        </div>
      </Link>
    </li>
  );
}

// Same confirm UX as DeleteDeckDialog, but for in-progress or errored
// builds. Copy adapts by status: a running/awaiting build is discarded
// (and any held credits refunded); an errored row is dismissed (credits
// were already refunded at error time).
function DiscardBuildDialog({
  build,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  build: BuildSummary | null;
  pending: boolean;
  error: Error | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  useEffect(() => {
    if (!build) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !pending) onCancel();
      if (e.key === "Enter" && !pending) onConfirm();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [build, pending, onCancel, onConfirm]);

  if (!build) return null;

  const isError = build.status === "error";
  const title = isError ? "Dismiss this errored build?" : "Discard this build?";
  const verb = isError ? "Dismissing…" : "Discarding…";
  const cta = isError ? "Dismiss" : "Discard";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="discard-build-title"
    >
      <div
        className="surface w-full max-w-md p-6 space-y-4 shadow-card-hover"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <h2 id="discard-build-title" className="text-base font-semibold">
            {title}
          </h2>
          <button
            onClick={onCancel}
            disabled={pending}
            className="text-fg-subtle hover:text-fg transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <X size={16} strokeWidth={1.75} />
          </button>
        </div>

        <div className="text-sm text-fg-muted space-y-2">
          <p>
            <span className="font-display text-fg">{build.brief}</span> will be
            removed from your in-progress list.
          </p>
          <p className="text-xs text-fg-subtle">
            {isError
              ? "Credits for this attempt were already refunded when it errored."
              : "Any credits held for this build are refunded."}
          </p>
        </div>

        {error && (
          <div className="text-xs text-danger">
            {error.message || "discard failed"}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            className="btn-secondary text-sm"
            onClick={onCancel}
            disabled={pending}
          >
            Cancel
          </button>
          <button
            className="btn-primary text-sm"
            onClick={onConfirm}
            disabled={pending}
          >
            {pending ? verb : cta}
          </button>
        </div>
      </div>
    </div>
  );
}
